import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { 
  GET_CLINICS, 
  GET_CLINIC, 
  GET_DOCTORS, 
  GET_DOCTOR, 
  GET_PATIENTS, 
  GET_PATIENTS_PAGINATED,
  GET_PATIENT, 
  GET_PROVIDERS, 
  GET_PROVIDER,
  GET_TAGS,
  GET_HK_REGIONS_AND_DISTRICTS,
  GET_CLINICS_BY_REGION,
  GET_DOCTORS_BY_SPECIALIZATION,
  GET_PROVIDERS_BY_CLINIC,
  GET_APPOINTMENTS,
  GET_APPOINTMENT_STATS,
  GET_DOCTOR_SPECIALIZATIONS,
  GET_APP_USERS,
  GET_APP_USERS_PAGINATED,
  GET_APP_USER,
  GET_SF_EXPRESS_ORDERS_FOR_APPOINTMENT,
  GET_EXPRESS_ORDERS_FOR_APPOINTMENT,
  SEARCH_SF_EXPRESS_ROUTES,
  SEARCH_SF_EXPRESS_ROUTES_BY_APPOINTMENT_ID
} from '../graphql/queries';
import {
  CREATE_CLINIC,
  UPDATE_CLINIC,
  DELETE_CLINIC,
  CREATE_DOCTOR,
  UPDATE_DOCTOR,
  DELETE_DOCTOR,
  CREATE_PATIENT,
  UPDATE_PATIENT,
  DELETE_PATIENT,
  CREATE_PROVIDER,
  UPDATE_PROVIDER,
  DELETE_PROVIDER,
  ADD_TAG_TO_PROVIDER,
  REMOVE_TAG_FROM_PROVIDER,
  CREATE_SCHEDULE,
  UPDATE_SCHEDULE,
  DELETE_SCHEDULE,
  CREATE_APPOINTMENT,
  UPDATE_APPOINTMENT,
  DELETE_APPOINTMENT,
  UPDATE_JOIN_STATUS,
  ADMIN_LOGIN,
  ADMIN_ME,
  ADMIN_REFRESH_TOKEN,
  CREATE_APP_USER,
  // UPDATE_APP_USER, // Not available in backend yet
  DELETE_APP_USER,
  SOFT_DELETE_APP_USER,
  RESTORE_APP_USER,
  CREATE_ADDRESS,
  UPDATE_ADDRESS,
  DELETE_ADDRESS,
  SET_DEFAULT_ADDRESS,
  ADMIN_CREATE_ADDRESS,
  ADMIN_UPDATE_ADDRESS,
  ADMIN_DELETE_ADDRESS,
  ADMIN_SET_DEFAULT_ADDRESS,
  DELETE_SF_EXPRESS_ORDER,
  DELETE_JD_EXPRESS_ORDER
} from '../graphql/mutations';
import { GetDoctorsQuery, CreateDoctorMutation, UpdateDoctorMutation } from '../generated/graphql';

// Clinic Hooks
export const useClinics = () => {
  return useQuery(GET_CLINICS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useClinic = (id: string) => {
  return useQuery(GET_CLINIC, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreateClinic = () => {
  return useMutation(CREATE_CLINIC, {
    refetchQueries: [{ query: GET_CLINICS }],
    errorPolicy: 'all'
  });
};

export const useUpdateClinic = () => {
  return useMutation(UPDATE_CLINIC, {
    refetchQueries: [{ query: GET_CLINICS }],
    errorPolicy: 'all'
  });
};

export const useDeleteClinic = () => {
  return useMutation(DELETE_CLINIC, {
    refetchQueries: [{ query: GET_CLINICS }],
    errorPolicy: 'all'
  });
};

// Doctor Hooks
export const useDoctors = () => {
  return useQuery(GET_DOCTORS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useDoctor = (id: string) => {
  return useQuery(GET_DOCTOR, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreateDoctor = () => {
  return useMutation(CREATE_DOCTOR, {
    refetchQueries: [{ query: GET_DOCTORS }],
    errorPolicy: 'all',
    update: (cache, { data }) => {
      console.log('Cache update function called for createDoctor:', { data });
      if (data?.createDoctor) {
        // Read existing doctors from cache
        const existingDoctors = cache.readQuery<GetDoctorsQuery>({ query: GET_DOCTORS });
        console.log('Existing doctors in cache:', existingDoctors);
        
        if (existingDoctors?.doctors) {
          // Write back to cache with new doctor added
          const newData = {
            doctors: [...existingDoctors.doctors, data.createDoctor]
          };
          console.log('Writing new data to cache:', newData);
          cache.writeQuery<GetDoctorsQuery>({
            query: GET_DOCTORS,
            data: newData
          });
        }
      }
    }
  });
};

export const useUpdateDoctor = () => {
  return useMutation(UPDATE_DOCTOR, {
    refetchQueries: [{ query: GET_DOCTORS }],
    errorPolicy: 'all',
    update: (cache, { data }) => {
      console.log('Cache update function called for updateDoctor:', { data });
      if (data?.updateDoctor) {
        // Read existing doctors from cache
        const existingDoctors = cache.readQuery<GetDoctorsQuery>({ query: GET_DOCTORS });
        console.log('Existing doctors in cache for update:', existingDoctors);
        
        if (existingDoctors?.doctors) {
          // Update the specific doctor in the cache
          const updatedDoctors = existingDoctors.doctors.map((doctor) =>
            doctor.id === data.updateDoctor.id ? data.updateDoctor : doctor
          );
          
          const newData = {
            doctors: updatedDoctors
          };
          console.log('Writing updated data to cache:', newData);
          
          cache.writeQuery<GetDoctorsQuery>({
            query: GET_DOCTORS,
            data: newData
          });
        }
      }
    }
  });
};

export const useDeleteDoctor = () => {
  return useMutation(DELETE_DOCTOR, {
    refetchQueries: [{ query: GET_DOCTORS }],
    errorPolicy: 'all'
  });
};

// Patient Hooks
export const usePatients = () => {
  return useLazyQuery(GET_PATIENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const usePatientsPaginated = (paginationDto: any) => {
  return useLazyQuery(GET_PATIENTS_PAGINATED, {
    variables: { paginationDto },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const usePatient = (id: string) => {
  return useQuery(GET_PATIENT, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreatePatient = () => {
  return useMutation(CREATE_PATIENT, {
    refetchQueries: [{ query: GET_PATIENTS }],
    errorPolicy: 'all'
  });
};

export const useUpdatePatient = () => {
  return useMutation(UPDATE_PATIENT, {
    refetchQueries: [{ query: GET_PATIENTS }],
    errorPolicy: 'all'
  });
};

export const useDeletePatient = () => {
  return useMutation(DELETE_PATIENT, {
    refetchQueries: [{ query: GET_PATIENTS }],
    errorPolicy: 'all'
  });
};

// Provider Hooks
export const useProviders = () => {
  return useQuery(GET_PROVIDERS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useProvider = (id: string) => {
  return useQuery(GET_PROVIDER, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreateProvider = () => {
  return useMutation(CREATE_PROVIDER, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useUpdateProvider = () => {
  return useMutation(UPDATE_PROVIDER, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useDeleteProvider = () => {
  return useMutation(DELETE_PROVIDER, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useAddTagToProvider = () => {
  return useMutation(ADD_TAG_TO_PROVIDER, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useRemoveTagFromProvider = () => {
  return useMutation(REMOVE_TAG_FROM_PROVIDER, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

// Tag Hooks
export const useTags = () => {
  return useQuery(GET_TAGS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

// Schedule Hooks
export const useCreateSchedule = () => {
  return useMutation(CREATE_SCHEDULE, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useUpdateSchedule = () => {
  return useMutation(UPDATE_SCHEDULE, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

export const useDeleteSchedule = () => {
  return useMutation(DELETE_SCHEDULE, {
    refetchQueries: [{ query: GET_PROVIDERS }],
    errorPolicy: 'all'
  });
};

// Appointment Hooks
export const useAppointments = () => {
  return useQuery(GET_APPOINTMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useAppointmentStats = () => {
  return useQuery(GET_APPOINTMENT_STATS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreateAppointment = () => {
  return useMutation(CREATE_APPOINTMENT, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: GET_APPOINTMENT_STATS }
    ],
    errorPolicy: 'all'
  });
};

export const useUpdateAppointment = () => {
  return useMutation(UPDATE_APPOINTMENT, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: GET_APPOINTMENT_STATS }
    ],
    errorPolicy: 'all'
  });
};

export const useDeleteAppointment = () => {
  return useMutation(DELETE_APPOINTMENT, {
    refetchQueries: [
      { query: GET_APPOINTMENTS },
      { query: GET_APPOINTMENT_STATS }
    ],
    errorPolicy: 'all'
  });
};

// Utility Hooks
export const useHKRegionsAndDistricts = () => {
  return useQuery(GET_HK_REGIONS_AND_DISTRICTS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
};

export const useDoctorSpecializations = (locale?: string) => {
  return useQuery(GET_DOCTOR_SPECIALIZATIONS, {
    variables: { locale },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });
};

// Lazy Query Hooks
export const useLazyClinicsByRegion = () => {
  return useLazyQuery(GET_CLINICS_BY_REGION, {
    errorPolicy: 'all'
  });
};

export const useLazyDoctorsBySpecialization = () => {
  return useLazyQuery(GET_DOCTORS_BY_SPECIALIZATION, {
    errorPolicy: 'all'
  });
};

export const useLazyProvidersByClinic = () => {
  return useLazyQuery(GET_PROVIDERS_BY_CLINIC, {
    errorPolicy: 'all'
  });
};

// Error handling utility
export const useGraphQLError = () => {
  const handleError = (error: any) => {
    console.error('GraphQL Error:', error);
    
    if (error.graphQLErrors) {
      return error.graphQLErrors.map((err: any) => err.message).join(', ');
    }
    
    if (error.networkError) {
      return 'Network error occurred. Please check your connection.';
    }
    
    return error.message || 'An unexpected error occurred.';
  };

  return { handleError };
}; 

// Join Status Hooks
export const useUpdateJoinStatus = () => {
  return useMutation(UPDATE_JOIN_STATUS, {
    errorPolicy: 'all'
  });
};


// Admin Authentication Hooks
export const useAdminLogin = () => {
  return useMutation(ADMIN_LOGIN, {
    errorPolicy: 'all'
  });
};

export const useAdminMe = () => {
  return useQuery(ADMIN_ME, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !localStorage.getItem('adminToken')
  });
};

export const useAdminRefreshToken = () => {
  return useMutation(ADMIN_REFRESH_TOKEN, {
    errorPolicy: 'all'
  });
};

// App User Hooks
export const useAppUsers = () => {
  return useQuery(GET_APP_USERS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useAppUsersPaginated = (paginationDto: any) => {
  return useQuery(GET_APP_USERS_PAGINATED, {
    variables: { paginationDto },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useAppUser = (id: string) => {
  return useQuery(GET_APP_USER, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useCreateAppUser = () => {
  return useMutation(CREATE_APP_USER, {
    refetchQueries: [
      { query: GET_APP_USERS }
    ],
    errorPolicy: 'all'
  });
};

// Note: Update user mutation not available in backend yet
// export const useUpdateAppUser = () => {
//   return useMutation(UPDATE_APP_USER, {
//     refetchQueries: [
//       { query: GET_APP_USERS }
//     ],
//     errorPolicy: 'all'
//   });
// };

export const useDeleteAppUser = () => {
  return useMutation(DELETE_APP_USER, {
    refetchQueries: [
      { query: GET_APP_USERS }
    ],
    errorPolicy: 'all'
  });
};

export const useSoftDeleteAppUser = () => {
  return useMutation(SOFT_DELETE_APP_USER, {
    refetchQueries: [
      { query: GET_APP_USERS }
    ],
    errorPolicy: 'all'
  });
};

export const useRestoreAppUser = () => {
  return useMutation(RESTORE_APP_USER, {
    refetchQueries: [
      { query: GET_APP_USERS }
    ],
    errorPolicy: 'all'
  });
};

// SF Express Hooks
export const useSfExpressOrdersForAppointment = (appointmentId: string) => {
  return useQuery(GET_SF_EXPRESS_ORDERS_FOR_APPOINTMENT, {
    variables: { appointmentId },
    skip: !appointmentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

// Unified Express Orders Hook
export const useExpressOrdersForAppointment = (appointmentId: string) => {
  return useQuery(GET_EXPRESS_ORDERS_FOR_APPOINTMENT, {
    variables: { appointmentId },
    skip: !appointmentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useSearchSfExpressRoutes = () => {
  return useLazyQuery(SEARCH_SF_EXPRESS_ROUTES, {
    errorPolicy: 'all'
  });
};

export const useSearchSfExpressRoutesByAppointmentId = () => {
  return useLazyQuery(SEARCH_SF_EXPRESS_ROUTES_BY_APPOINTMENT_ID, {
    errorPolicy: 'all'
  });
};

// SF Express Delete Hook
export const useDeleteSfExpressOrder = () => {
  return useMutation(DELETE_SF_EXPRESS_ORDER, {
    errorPolicy: 'all'
  });
};

// JD Express Delete Hook
export const useDeleteJdExpressOrder = () => {
  return useMutation(DELETE_JD_EXPRESS_ORDER, {
    errorPolicy: 'all'
  });
};

// Address Hooks

export const useCreateAddress = () => {
  return useMutation(CREATE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useUpdateAddress = () => {
  return useMutation(UPDATE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useDeleteAddress = () => {
  return useMutation(DELETE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useSetDefaultAddress = () => {
  return useMutation(SET_DEFAULT_ADDRESS, {
    errorPolicy: 'all'
  });
};

// Admin Address Hooks
export const useAdminCreateAddress = () => {
  return useMutation(ADMIN_CREATE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useAdminUpdateAddress = () => {
  return useMutation(ADMIN_UPDATE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useAdminDeleteAddress = () => {
  return useMutation(ADMIN_DELETE_ADDRESS, {
    errorPolicy: 'all'
  });
};

export const useAdminSetDefaultAddress = () => {
  return useMutation(ADMIN_SET_DEFAULT_ADDRESS, {
    errorPolicy: 'all'
  });
}; 